import { useEffect, useMemo, useState } from 'react'
import { toast } from 'sonner'
import { Slash } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { ConfirmButton, SectionLabel } from '@/components/bookings/dialogKit'
import { createTimeBlock } from '@/lib/api'
import { readApiError } from '@/lib/apiError'
import { fmtDayDate } from '@/lib/format'
import { localDateTime, minutesToLabel, type SlotPick } from '@/lib/slotBooking'
import { cn } from '@/lib/utils'

// ─── "Block time" ──────────────────────────────────────────────────────────────
//
// Closes a window so nothing can be booked in it, starting from the slot that was
// clicked. This is the OPPOSITE of a calendar memo and the copy has to keep
// saying so: a block stops the business selling that time, a memo does not.
//
// The delivery surface is new; the capability is not. add_time_block has existed
// in the API, in operator chat and over MCP from the start, so there is no
// parity change to make here.
//
// TWO SCOPES, AND THEY ARE NOT INTERCHANGEABLE. Clicking a machine's column
// closes THAT machine while the rest of the centre keeps trading; clicking the
// Unassigned column (which is not a machine) closes the WHOLE BUSINESS. The
// dialog names which one it is about to do in the heading, in the confirm button
// and in the toast, because getting it wrong either loses a day's trade or
// leaves a broken machine bookable.
//
// A block always ends up as an explicit block_start/block_end pair. Duration is
// how an operator thinks about it ("that machine's out for two hours") and is
// only a way to reach the end time.

/** Duration units, largest step last. Each is a CHOICE, never a typed unit. */
const UNITS = [
  { key: 'minutes', label: 'Minutes', minutes: 1 },
  { key: 'hours', label: 'Hours', minutes: 60 },
  { key: 'days', label: 'Days', minutes: 60 * 24 },
] as const

type UnitKey = typeof UNITS[number]['key']

export interface BlockTimeDialogProps {
  pick: SlotPick | null
  onOpenChange: (open: boolean) => void
  /** Refetch the calendar once the closure lands. */
  onCreated?: () => void
}

export function BlockTimeDialog({ pick, onOpenChange, onCreated }: BlockTimeDialogProps) {
  const open = pick !== null

  const [amount, setAmount] = useState('1')
  const [unit, setUnit] = useState<UnitKey>('hours')
  const [reason, setReason] = useState('')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!open) return
    setAmount('1')
    setUnit('hours')
    setReason('')
    setSaving(false)
    setError(null)
  }, [open, pick?.date, pick?.startMinutes, pick?.resourceId])

  const parsedAmount = Number(amount)
  const validAmount = Number.isFinite(parsedAmount) && parsedAmount > 0
  const durationMinutes = validAmount
    ? Math.round(parsedAmount * (UNITS.find(u => u.key === unit)?.minutes ?? 60))
    : 0

  // The end, expressed in minutes from midnight of the CLICKED day. A multi-day
  // closure runs past 1440, which is exactly what a naive-local datetime cannot
  // express — so the date is rolled forward and the remainder becomes the time.
  const end = useMemo(() => {
    if (!pick || durationMinutes <= 0) return null
    const total = pick.startMinutes + durationMinutes
    const dayOffset = Math.floor(total / (60 * 24))
    const minutesIntoDay = total % (60 * 24)
    const [y, m, d] = pick.date.split('-').map(Number)
    // Date maths in UTC so the browser's own timezone cannot shift the day.
    const shifted = new Date(Date.UTC(y, m - 1, d + dayOffset))
    return { date: shifted.toISOString().slice(0, 10), minutes: minutesIntoDay }
  }, [pick, durationMinutes])

  const wholeBusiness = pick?.resourceId === null
  const scopeLabel = wholeBusiness ? 'the whole centre' : pick?.resourceName ?? 'this resource'

  const canSave = pick !== null && end !== null && validAmount && !saving

  async function save() {
    if (!canSave || !pick || !end) return
    setSaving(true)
    setError(null)
    try {
      await createTimeBlock({
        block_start: localDateTime(pick.date, pick.startMinutes),
        block_end: localDateTime(end.date, end.minutes),
        resource_id: pick.resourceId,
        reason: reason.trim() || null,
      })
      toast.success(
        wholeBusiness ? 'The centre is closed for that window' : `${scopeLabel} is blocked out`,
        {
          description: `${fmtDayDate(pick.date)}, ${minutesToLabel(pick.startMinutes)} for ${amount} ${unit}.`,
        },
      )
      onCreated?.()
      onOpenChange(false)
    } catch (e) {
      setError(readApiError(e))
      setSaving(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={(next) => { if (!next) onOpenChange(false) }}>
      <DialogContent className="max-h-[85vh] max-w-md overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Block time</DialogTitle>
          <DialogDescription>
            {pick ? `${fmtDayDate(pick.date)} · from ${minutesToLabel(pick.startMinutes)}` : ''}
          </DialogDescription>
        </DialogHeader>

        {pick ? (
          <div className="flex flex-col gap-4">
            {/* ── Scope ───────────────────────────────────────────────────── */}
            <div className="flex flex-col gap-1.5">
              <SectionLabel>What closes</SectionLabel>
              <p
                className={cn(
                  'rounded-lg border px-3 py-2 text-[13px]',
                  wholeBusiness
                    ? 'border-amber-500/50 bg-amber-500/10 text-foreground'
                    : 'border-border bg-muted text-foreground',
                )}
              >
                {wholeBusiness ? (
                  <>
                    <span className="font-semibold">The whole centre.</span> Nothing at all can be
                    booked in this window, on any machine.
                  </>
                ) : (
                  <>
                    <span className="font-semibold">{scopeLabel}</span> only. The rest of the centre
                    keeps taking bookings.
                  </>
                )}
              </p>
            </div>

            {/* ── How long ────────────────────────────────────────────────── */}
            <div className="flex flex-col gap-1.5">
              <SectionLabel>For how long</SectionLabel>
              <div className="flex items-stretch gap-2">
                <input
                  type="number"
                  min={1}
                  inputMode="numeric"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  aria-label="Duration"
                  className="w-20 rounded-lg border border-border bg-background px-2.5 py-1.5 text-[14px] text-foreground outline-none focus:border-primary"
                />
                <div className="flex flex-1 gap-1.5">
                  {UNITS.map((u) => (
                    <button
                      key={u.key}
                      type="button"
                      aria-pressed={unit === u.key}
                      onClick={() => setUnit(u.key)}
                      className={cn(
                        'flex-1 rounded-lg border px-2 py-1.5 text-[12.5px] font-semibold transition-colors',
                        unit === u.key
                          ? 'border-primary bg-primary/10 text-foreground'
                          : 'border-border bg-card text-muted-foreground hover:bg-muted',
                      )}
                    >
                      {u.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Spelling the end out is the only way an operator can catch a
                  mistyped duration before it takes a machine off the diary for a
                  fortnight. */}
              {end ? (
                <p className="text-[11.5px] text-muted-foreground">
                  Closed until <span className="font-semibold text-foreground">
                    {minutesToLabel(end.minutes === 0 ? 0 : end.minutes)}
                  </span>
                  {end.date !== pick.date ? ` on ${fmtDayDate(end.date)}` : ''}.
                </p>
              ) : (
                <p className="text-[11.5px] text-destructive">Enter how long it should stay closed.</p>
              )}
            </div>

            {/* ── Reason ──────────────────────────────────────────────────── */}
            <div className="flex flex-col gap-1.5">
              <SectionLabel>Reason (optional)</SectionLabel>
              {/* Free text, because a closure's reason is genuinely open — "waiting
                  on a part", "Ella's wedding". It is a label on the Time blocks
                  screen and nothing reads it as data. */}
              <input
                type="text"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Public holiday, maintenance…"
                aria-label="Reason"
                className="w-full rounded-lg border border-border bg-background px-2.5 py-1.5 text-[14px] text-foreground outline-none placeholder:text-muted-foreground focus:border-primary"
              />
            </div>

            {error ? <p className="text-[12.5px] text-destructive">{error}</p> : null}

            <div className="flex flex-col gap-2 border-t border-border pt-3">
              <ConfirmButton
                label={wholeBusiness ? 'Close the centre' : `Block ${scopeLabel}`}
                icon={<Slash className="size-4" aria-hidden />}
                busy={saving}
                disabled={!canSave}
                onClick={() => void save()}
              />
              <p className="text-center text-[11px] text-muted-foreground">
                This stops bookings. To leave a reminder without holding the time, add a note
                instead.
              </p>
            </div>
          </div>
        ) : null}
      </DialogContent>
    </Dialog>
  )
}
